/**
 * GitHub App Installation Callback Page
 *
 * After a tenant admin installs the GitHub App, GitHub redirects to
 * this Setup URL with `?installation_id=XXX&setup_action=install`.
 *
 * The page lets the admin confirm the account info and records the
 * installation via POST /github/installations.
 *
 * Route: /knowledge/datasources/github/installed
 */

import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Github } from 'lucide-react';
import {
  useCreateGitHubInstallation,
  getErrorMessage,
} from '../hooks/use-oauth-apps';

export function GitHubInstalledPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const createInstallation = useCreateGitHubInstallation();

  const installationId = searchParams.get('installation_id');
  const setupAction = searchParams.get('setup_action');

  const [accountLogin, setAccountLogin] = useState('');
  const [accountType, setAccountType] = useState<'Organization' | 'User'>('Organization');
  const [submitted, setSubmitted] = useState(false);

  // If no installation_id, show error
  if (!installationId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Missing Installation ID
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            This page is the callback for GitHub App installation.
            It should be accessed via the GitHub App install flow.
          </p>
          <button
            onClick={() => navigate('/settings/integrations')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Go to Integrations
          </button>
        </div>
      </div>
    );
  }

  // Show success after submission
  if (submitted && createInstallation.isSuccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            GitHub App Installed
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            The installation for <strong>{accountLogin}</strong> has been recorded.
            You can now create data sources from GitHub repositories.
          </p>
          <button
            onClick={() => navigate('/settings/integrations')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
          >
            Go to Integrations
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountLogin.trim()) return;
    createInstallation.mutate(
      {
        installation_id: parseInt(installationId, 10),
        account_login: accountLogin.trim(),
        account_type: accountType,
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Github className="mx-auto h-12 w-12 text-gray-800 dark:text-gray-200 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Confirm GitHub App Installation
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            The GitHub App has been {setupAction === 'update' ? 'updated' : 'installed'}.
            Please confirm the account details below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Installation ID (read-only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Installation ID
            </label>
            <input
              type="text"
              value={installationId}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm"
            />
          </div>

          {/* Account Login */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Organization or User Name
            </label>
            <input
              type="text"
              value={accountLogin}
              onChange={(e) => setAccountLogin(e.target.value)}
              placeholder="e.g. contoso-org"
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">
              The GitHub organization or user where you just installed the app.
            </p>
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Account Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="accountType"
                  value="Organization"
                  checked={accountType === 'Organization'}
                  onChange={() => setAccountType('Organization')}
                  className="text-blue-600"
                />
                Organization
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="radio"
                  name="accountType"
                  value="User"
                  checked={accountType === 'User'}
                  onChange={() => setAccountType('User')}
                  className="text-blue-600"
                />
                User
              </label>
            </div>
          </div>

          {/* Error */}
          {createInstallation.error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-300">
              {getErrorMessage(createInstallation.error)}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={!accountLogin.trim() || createInstallation.isPending}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors"
            >
              {createInstallation.isPending ? 'Recording...' : 'Confirm Installation'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/settings/integrations')}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
            >
              Skip
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
